import { ref, readonly, h, defineComponent } from "vue";
import AddNotifyDialogComponent from '@/components/dialogs/AddNotifyDialog.vue';
import repository from '@/repositories/repositoryFactory';
import { NOTIFY_TYPE } from "@/const/codes";

// ステート
const modal = ref({
    isSent: false, // 送信済みか？
    isInvalidToSent: false, // バリデーションフラグ：通知先
    isInvalidAuthCode: false, // バリデーションフラグ：認証コード
    notifyTypeList: [
        { key: NOTIFY_TYPE.SMS, label: 'SMS' },
        { key: NOTIFY_TYPE.MAIL, label: 'メール' }]
});

// 現在選択している通知先
const selectedNotifyType = ref(NOTIFY_TYPE.SMS as number);

// 認証コード
const authCode = ref("");

// 送信先
const toSent = ref("");

// ダイアログオープン制御フラグ
const isOpen = ref(false);

// 戻り値
const _resolve = ref<(ans: boolean | undefined) => void>(() => { })

/**
 * AddNotifyDialog
 */
export default function useAddNotifyDialog() {

    /**
     * 確認ダイアログを開く：ボタンが押されたタイミングで、結果を返す
     * @throws API呼出し等のエラー
     * @return Promise<number | undefined>
     */
    const openDialog = async () => {

        // 状態のセット
        modal.value.isSent = false;
        modal.value.isInvalidToSent = false;
        modal.value.isInvalidAuthCode = false;
        toSent.value = "";
        authCode.value = "";
        selectedNotifyType.value = NOTIFY_TYPE.SMS;

        // ダイアログオープン
        isOpen.value = true;

        return new Promise<boolean | undefined>((resolve) => {
            _resolve.value = resolve
        })
    };

    /**
     * ダイアログを閉じる
     */
    const closeDialog = (result: boolean | undefined) => {
        // 結果を返却
        _resolve.value(result);

        // ステートリセット
        modal.value.isSent = false;
        modal.value.isInvalidToSent = false;
        modal.value.isInvalidAuthCode = false;
        toSent.value = "";
        authCode.value = "";
        selectedNotifyType.value = NOTIFY_TYPE.SMS;

        // モーダルを閉じる
        isOpen.value = false;
        _resolve.value = () => { }
    };

    /**
     * 認証コードを送信する
     */
    const sendNotify = async () => {
        modal.value.isInvalidToSent = false;

        // 送信先のバリデーション
        if (!validationToSent()) {
            modal.value.isInvalidToSent = true;
            return;
        }

        try {
            // 情報をセット
            modal.value.isSent = true;
            // 通知送信
            await repository.auth.sendAuthCode(toSent.value, selectedNotifyType.value);
        }
        catch (error) {
            // ログ出力するのみ
            if (error instanceof Error) {
                // Errorの場合
                console.log(error.message);
            }
        }
    };

    /**
     * 通知先を登録する
     */
    const registNotify = async () => {
        let ret: boolean;

        modal.value.isInvalidAuthCode = false;

        // 認証コードチェック
        const code = parseInt(authCode.value);
        if (isNaN(code)) {
            modal.value.isInvalidAuthCode = true;
            return;
        }

        try {
            // 通知先登録
            ret = await repository.company.addNotices(toSent.value, selectedNotifyType.value, authCode.value);
        }
        catch {
            ret = false;
        }

        // モーダルを閉じる
        closeDialog(ret);
    };

    /**
     * 送信先のバリデーション
     */
    const validationToSent = (): boolean => {
        let ret = false;
        let regExp: RegExp;

        switch (selectedNotifyType.value) {

            case NOTIFY_TYPE.SMS:
                regExp = new RegExp('^0[789]0\\d{8}$');
                ret = regExp.test(toSent.value);
                break;

            case NOTIFY_TYPE.MAIL:
                regExp = new RegExp('[\\w\\-\\._]+@[\\w\\-\\._]+\\.[A-Za-z]+');
                ret = regExp.test(toSent.value);
                break;
        }

        return ret;
    };

    // ダイアログコンポーネントの描写
    const render = () => h(AddNotifyDialogComponent);
    const AddNotifyDialog = defineComponent({ render });

    return {
        // data
        isOpen: readonly(isOpen),
        state: readonly(modal),
        selectedNotifyType,
        authCode,
        toSent,

        // methods
        openAddNotifyDialog: openDialog,
        closeAddNotifyDialog: closeDialog,
        sendNotify,
        registNotify,

        // components
        AddNotifyDialog
    };
}